import { useEffect, useCallback } from "react";
import { getFullNodePositions } from "../drawNode/drawNode";
import type { TreeNodeData } from "../TreeViewDataLayer";

interface UseTreeViewNavigationProps {
  aboData: TreeNodeData | null;
  canvasSize: { width: number; height: number };
  zoom: number;
  setZoom: (zoom: number) => void;
  setPan: (pan: { x: number; y: number }) => void;
  setAboData: React.Dispatch<React.SetStateAction<TreeNodeData | null>>;
  pendingPanToNodeId: string | null;
  setPendingPanToNodeId: (id: string | null) => void;
}

interface UseTreeViewNavigationReturn {
  panToNode: (targetId: string, shouldZoom?: boolean) => void;
  expandPathToNode: (targetId: string) => void;
}

/**
 * Hook to handle tree view navigation including panning to nodes and expanding paths
 */
export const useTreeViewNavigation = ({
  aboData,
  canvasSize,
  zoom,
  setZoom,
  setPan,
  setAboData,
  pendingPanToNodeId,
  setPendingPanToNodeId,
}: UseTreeViewNavigationProps): UseTreeViewNavigationReturn => {
  const calculateNodePosition = useCallback(
    (targetId: string, rootData: TreeNodeData, rootX: number, rootY: number): { x: number; y: number } | null => {
      const positions = getFullNodePositions(rootData, rootX, rootY);
      const targetAbo = parseInt(targetId, 10);
      return positions.get(targetAbo) ?? null;
    },
    [],
  );

  const panToNode = useCallback(
    (targetId: string, shouldZoom = true) => {
      if (canvasSize.width === 0 || !aboData) return;

      const rootX = canvasSize.width / 2;
      const rootY = 80;
      const position = calculateNodePosition(targetId, aboData, rootX, rootY);

      if (position) {
        // Reset zoom to 1 when requested (e.g. search navigation) so the node is
        // clearly visible regardless of whether the user was zoomed in or out.
        if (shouldZoom && zoom !== 1) {
          setZoom(1);
        }

        const ceoNodeY = 80;
        const viewCenterY = canvasSize.height / 2;
        const offsetY = ceoNodeY - viewCenterY;

        // Calculate pan to center the node in the viewport
        // The view center formula in useCanvasRendering is:
        // paper.view.center = new paper.Point(canvasSize.width / 2 - pan.x, canvasSize.height / 2 - pan.y + offsetY)
        // To center on position, we need view.center = position
        // So: canvasSize.width / 2 - pan.x = position.x
        // Therefore: pan.x = canvasSize.width / 2 - position.x
        setPan({
          x: canvasSize.width / 2 - position.x,
          y: canvasSize.height / 2 - position.y + offsetY,
        });
      }
    },
    [aboData, calculateNodePosition, setPan, setZoom, canvasSize, zoom],
  );

  const expandPathToNode = useCallback(
    (targetId: string) => {
      if (!aboData) return;

      const findPath = (node: TreeNodeData, path: string[] = []): string[] | null => {
        if (node.abo.toString() === targetId) return path;
        for (const child of node.downlines ?? []) {
          const result = findPath(child, [...path, node.abo.toString()]);
          if (result) return result;
        }
        return null;
      };

      const foundPath = findPath(aboData);
      if (foundPath) {
        setAboData((prevData) => {
          if (!prevData) return prevData;
          const expandNodes = (node: TreeNodeData): TreeNodeData => ({
            ...node,
            expanded: foundPath.includes(node.abo.toString()) ? true : node.expanded,
            downlines: node.downlines?.map(expandNodes),
          });
          return expandNodes(prevData);
        });
      }
    },
    [aboData, setAboData],
  );

  useEffect(() => {
    if (pendingPanToNodeId) {
      const timeoutId = setTimeout(() => {
        panToNode(pendingPanToNodeId, false);
        setPendingPanToNodeId(null);
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [pendingPanToNodeId, aboData, panToNode, setPendingPanToNodeId]);

  return { panToNode, expandPathToNode };
};
